import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Alert from "@cx/ui/Alert";
import Button from "@cx/ui/Button";
import Badge from "@cx/ui/Badge";
import CheckBoxList from "@cx/ui/CheckBoxList";
import SubmitButton from "@cx/ui/SubmitButton";
import LoadingIndicator from "@cx/ui/LoadingIndicator";
import IconWarning from "@cx/ui/Icons/IconWarning";
import IconArrowDownward from "@cx/ui/Icons/IconArrowDownward";
import IconInfoOutline from "@cx/ui/Icons/IconInfoOutline";
import Tooltip from "@cx/ui/Tooltip";
import AccordionGroup from "@cx/ui/AccordionGroup";
import HeaderBar from "../Header";
import ToastMessage from "../common/ToastMessage";
import {
  enrichSolutionsWithMetadata,
  getSolutionName
} from "../../utils/solutionUtils";
import "./_mergeSummaryPage.scss";

// Shared propType shapes to avoid duplication
const solutionLinkShape = PropTypes.shape({
  solutionId: PropTypes.string.isRequired,
  principalIdentifier: PropTypes.string
});

const enrichedSolutionShape = PropTypes.shape({
  name: PropTypes.string,
  logoUrl: PropTypes.string,
  id: PropTypes.string,
  principalIdentifier: PropTypes.string
});

// Base account shape with common fields (for input props with solutionLinks)
const baseAccountShape = {
  displayName: PropTypes.string.isRequired,
  username: PropTypes.string.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  solutionLinks: PropTypes.arrayOf(solutionLinkShape)
};

// Account shape (same for all accounts now - no createdOn)
const accountShape = PropTypes.shape(baseAccountShape);

// Enriched account shape (after processing with solutions metadata)
const enrichedAccountShape = PropTypes.shape({
  displayName: PropTypes.string.isRequired,
  username: PropTypes.string.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  solutions: PropTypes.arrayOf(enrichedSolutionShape)
});

// Minimal account shape for header (only needs displayName and username)
const accountHeaderShape = PropTypes.shape({
  displayName: PropTypes.string.isRequired,
  username: PropTypes.string.isRequired
});

// Shared component for rendering solutions list
const SolutionsList = ({ solutions, t }) => (
  <div className="solutions-section">
    <span className="solutions-label">
      {t("merge-summary.solutions")} ({solutions?.length || 0})
      <Tooltip
        htmlId="solutions-info-tooltip"
        tooltipContent={t(
          "merge-summary.solutions-tooltip",
          "Only enabled solutions shown"
        )}
        position="right"
      >
        <IconInfoOutline className="icon-info" htmlId="solutions-info-icon" />
      </Tooltip>
    </span>
    <div className="solutions-list">
      {solutions?.map((solution, index) => (
        <div key={solution.id || index} className="solution-item">
          <div className="solution-logo-container">
            {solution.logoUrl ? (
              <img
                src={solution.logoUrl}
                alt={solution.name}
                className="solution-logo"
              />
            ) : (
              <span className="solution-name-fallback">
                {solution.name || "—"}
              </span>
            )}
          </div>
          <div className="solution-info">
            <span className="solution-name">
              {t("merge-summary.solution-label")}: {solution.name || "—"}
            </span>
            <span className="principal-id">
              {t("merge-summary.your-id-label")}:{" "}
              {solution.principalIdentifier || "—"}
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

SolutionsList.propTypes = {
  solutions: PropTypes.arrayOf(enrichedSolutionShape),
  t: PropTypes.func.isRequired
};

const AccountHeader = ({
  account,
  label,
  labelColor,
  warningText,
  swapLink
}) => (
  <div className="account-header-content">
    <div className="account-identity">
      <span className="account-name">{account.displayName}</span>
      <div className="account-actions">
        <Badge htmlId={`account-label-${account.username}`} color={labelColor}>
          {label}
        </Badge>
        {swapLink}
      </div>
    </div>
    {warningText && (
      <span className="account-warning">
        <IconWarning
          htmlId={`warning-icon-${account.username}`}
          className="warning-icon"
          style={{ marginRight: 0 }}
        />
        {warningText}
      </span>
    )}
  </div>
);

AccountHeader.propTypes = {
  account: accountHeaderShape.isRequired,
  label: PropTypes.string.isRequired,
  labelColor: PropTypes.string,
  swapLink: PropTypes.node,
  warningText: PropTypes.string
};

// Shared component for rendering a single detail item
const DetailItem = ({ label, value, className }) => (
  <div className={className ? `detail-item ${className}` : "detail-item"}>
    <span className="detail-label">{label}</span>
    <span className="detail-value">{value}</span>
  </div>
);

DetailItem.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  value: PropTypes.string
};

const AccountDetails = ({ account, t }) => (
  <div className="account-details">
    <div className="details-left">
      <div className="details-grid">
        <DetailItem label={t("common.username")} value={account.username} />
        <DetailItem
          label={t("common.first-name")}
          value={account.firstName || "-"}
        />
        <DetailItem label={t("common.email")} value={account.email || "-"} />
        <DetailItem
          label={t("common.last-name")}
          value={account.lastName || "-"}
        />
      </div>
    </div>
    <div className="details-right">
      <SolutionsList solutions={account.solutions} t={t} />
    </div>
  </div>
);

AccountDetails.propTypes = {
  account: enrichedAccountShape.isRequired,
  t: PropTypes.func.isRequired
};

const CombinedAccountDetails = ({ account, t }) => (
  <div className="account-details combined-details">
    <div className="combined-left">
      <div className="account-name-display">
        <span className="display-name">{account.displayName}</span>
        <Badge
          htmlId={`account-label-${account.username}-combined-details`}
          color="blue"
          className="account-label-details"
        >
          {t("merge-summary.combined-account")}
        </Badge>
      </div>
      <div className="details-grid">
        <DetailItem label={t("common.username")} value={account.username} />
        <DetailItem
          label={t("common.first-name")}
          value={account.firstName || "-"}
        />
        <DetailItem label={t("common.email")} value={account.email || "-"} />
        <DetailItem
          label={t("common.last-name")}
          value={account.lastName || "-"}
        />
      </div>
    </div>
    <div className="combined-right">
      <SolutionsList solutions={account.solutions} t={t} />
    </div>
  </div>
);

CombinedAccountDetails.propTypes = {
  account: enrichedAccountShape.isRequired,
  t: PropTypes.func.isRequired
};

const FlowIndicator = ({ text, id }) => (
  <div className="flow-indicator">
    <IconArrowDownward
      htmlId={`flow-arrow-${id}`}
      className="flow-icon"
      size="medium"
    />
    <span className="flow-text">{text}</span>
  </div>
);

FlowIndicator.propTypes = {
  id: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired
};

// Custom hook to enrich account with solution metadata
const useEnrichedAccount = (account) =>
  useMemo(
    () => ({
      ...account,
      solutions: enrichSolutionsWithMetadata(account.solutionLinks)
    }),
    [account]
  );

// Known blocking reason codes - moved outside component to avoid recreation on each render
const KNOWN_BLOCKING_REASONS = [
  "SAME_USER",
  "CONFLICTING_SOLUTIONS",
  "HAS_FEDERATION",
  "HAS_CENTRALLY_MANAGED_PRODUCTS"
];

// Lock the UI 5 minutes before the server-side session actually expires.
// This gives the merge API call enough runway to complete without the BFF
// rejecting it mid-flight due to an expired session.
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

const MergeSummaryPage = ({
  secondAccount,
  primaryAccount,
  combinedAccount,
  onSubmit = () => {},
  onCancel = () => {},
  onSwap = () => {},
  onResetSwap = () => {},
  isSubmitting = false,
  isLoadingSwap = false,
  isSwapped = false,
  isEligible = true,
  conflicts = [],
  swappedEligibility = null,
  expiresAt = null
}) => {
  const { t } = useTranslation();
  const [consentChecked, setConsentChecked] = useState(false);

  // Check if the merge session has expired (or is about to expire).
  // Uses state + interval so the UI reacts before the server-side session
  // actually expires, giving in-flight API calls time to complete.
  const [isExpired, setIsExpired] = useState(() => {
    if (!expiresAt) return false;
    return new Date(expiresAt) - new Date() < EXPIRY_BUFFER_MS;
  });

  useEffect(() => {
    if (!expiresAt) {
      setIsExpired(false);
      return;
    }

    const checkExpiry = () =>
      setIsExpired(new Date(expiresAt) - new Date() < EXPIRY_BUFFER_MS);
    checkExpiry();

    // Re-check every 30 seconds so the UI locks within ~30s of the buffer threshold
    const intervalId = setInterval(checkExpiry, 30_000);
    return () => clearInterval(intervalId);
  }, [expiresAt]);

  // Determine effective eligibility based on swap state
  const effectiveEligibility = useMemo(() => {
    if (!isSwapped) {
      // Default direction - use original eligibility
      return { isEligible, conflicts };
    }
    // Swapped direction - use swapped eligibility if available
    if (swappedEligibility) {
      return {
        ...swappedEligibility,
        conflicts: swappedEligibility.conflicts || []
      };
    }
    // Still loading swapped eligibility - keep showing original until loaded
    return { isEligible, conflicts };
  }, [isSwapped, isEligible, conflicts, swappedEligibility]);

  // Determine if the consent checkbox should be disabled (blocked or expired)
  const isConsentDisabled =
    !effectiveEligibility.isEligible || isExpired || isLoadingSwap;

  // Determine if submit should be disabled
  const isSubmitDisabled = !consentChecked || isConsentDisabled || isSubmitting;

  // Enrich solution links with metadata from appConfig.solutionConfig
  const enrichedSecondAccount = useEnrichedAccount(secondAccount);
  const enrichedPrimaryAccount = useEnrichedAccount(primaryAccount);
  const enrichedCombinedAccount = useEnrichedAccount(combinedAccount);

  // Determine which account is surviving (keeps credentials) vs non-surviving (deactivated)
  // When swapped, the second account becomes the surviving account
  const survivingAccount = isSwapped
    ? enrichedSecondAccount
    : enrichedPrimaryAccount;
  const nonSurvivingAccount = isSwapped
    ? enrichedPrimaryAccount
    : enrichedSecondAccount;

  // Build the combined account display: use the surviving account's profile
  // (name, email, username) but override solutions with the merged set from
  // the API response, which represents what the surviving account will have
  // after the merge completes.
  const displayCombinedAccount = useMemo(
    () => ({
      ...survivingAccount,
      solutions: enrichedCombinedAccount.solutions
    }),
    [survivingAccount, enrichedCombinedAccount.solutions]
  );

  // Handle swap button click
  const handleSwap = useCallback(() => {
    // Reset consent when swapping since the user needs to review the new configuration
    setConsentChecked(false);
    if (isSwapped) {
      // Swapping back to original - dispatch action to toggle in Redux
      onResetSwap();
    } else {
      // Swapping to other direction - fetch eligibility (Redux will toggle on success)
      onSwap();
    }
  }, [isSwapped, onResetSwap, onSwap]);

  // Map conflict objects to user-friendly messages.
  // The backend provides both the reason code and details (e.g. which solutions conflict).
  const getConflictMessage = useCallback(
    (conflict) => {
      const reasonKey = KNOWN_BLOCKING_REASONS.includes(conflict.reason)
        ? conflict.reason
        : "UNKNOWN";

      if (reasonKey === "CONFLICTING_SOLUTIONS") {
        const ids = (conflict.details?.conflictingSolutionIds || []).filter(
          Boolean
        );
        const solutionNames = ids.map(getSolutionName).join(", ") || "\u2014";
        return t(`merge.blocking-reasons.${reasonKey}`, {
          solutionNames,
          interpolation: { escapeValue: false }
        });
      }

      return t(`merge.blocking-reasons.${reasonKey}`);
    },
    [t]
  );

  const handleSubmit = useCallback(() => {
    // Double-check expiry at submit time to close the ~30s polling window.
    // Uses the same buffer so we don't send a request that will expire mid-flight.
    if (expiresAt && new Date(expiresAt) - new Date() < EXPIRY_BUFFER_MS) {
      setIsExpired(true);
      return;
    }
    if (!isSubmitDisabled && onSubmit) {
      onSubmit(isSwapped);
    }
  }, [isSubmitDisabled, onSubmit, isSwapped, expiresAt]);

  // Memoize swap link to avoid recreation on every render
  const swapLinkElement = useMemo(() => {
    if (isExpired) return null;
    if (isLoadingSwap) {
      return (
        <LoadingIndicator
          htmlId="swap-loading-indicator"
          size="small"
          className="swap-loading-indicator"
        />
      );
    }
    return (
      <button
        type="button"
        className="swap-accounts-link"
        aria-label={t("merge-summary.swap-accounts-link")}
        onClick={(e) => {
          e.preventDefault();
          // Prevent accordion header from toggling when clicking swap link
          e.stopPropagation();
          handleSwap();
        }}
      >
        {t("merge-summary.swap-accounts-link")}
      </button>
    );
  }, [isExpired, isLoadingSwap, handleSwap, t]);

  return (
    <div className="merge-summary-page">
      <ToastMessage />
      <HeaderBar />
      <div className="merge-summary-content">
        <h1 className="page-title">{t("merge-summary.title")}</h1>
        <p className="page-description">{t("merge-summary.description")}</p>

        <div className="accounts-flow">
          <AccordionGroup.Container
            htmlId="non-surviving-account-accordion"
            independent
            className="account-accordion"
          >
            <AccordionGroup.Item
              htmlId="non-surviving-account-item"
              eventKey="non-surviving"
              defaultExpanded
              header={
                <AccountHeader
                  account={nonSurvivingAccount}
                  label={t("merge-summary.account-to-deactivate")}
                  labelColor="red"
                  warningText={t("merge-summary.credentials-no-longer-used")}
                  swapLink={swapLinkElement}
                />
              }
            >
              <AccountDetails account={nonSurvivingAccount} t={t} />
            </AccordionGroup.Item>
          </AccordionGroup.Container>

          <FlowIndicator
            id="merge-into"
            text={t("merge-summary.to-be-merged-into")}
          />

          <AccordionGroup.Container
            htmlId="surviving-account-accordion"
            independent
            className="account-accordion"
          >
            <AccordionGroup.Item
              htmlId="surviving-account-item"
              eventKey="surviving"
              defaultExpanded
              header={
                <AccountHeader
                  account={survivingAccount}
                  label={t("merge-summary.account-to-keep")}
                  labelColor="green"
                  warningText={t("merge-summary.credentials-will-be-kept")}
                />
              }
            >
              <AccountDetails account={survivingAccount} t={t} />
            </AccordionGroup.Item>
          </AccordionGroup.Container>

          <FlowIndicator
            id="resulting"
            text={t("merge-summary.resulting-in")}
          />

          <div className="account-accordion combined-account">
            <CombinedAccountDetails account={displayCombinedAccount} t={t} />
          </div>
        </div>

        <div className="after-submitting-section">
          <h2 className="section-title">
            {t("merge-summary.after-submitting")}
          </h2>
          <ul className="benefits-list">
            <li>{t("merge-summary.one-profile-benefit")}</li>
            <li>{t("merge-summary.all-solutions-benefit")}</li>
          </ul>
        </div>

        {(!effectiveEligibility.isEligible || isExpired) && !isLoadingSwap && (
          <Alert
            htmlId="blocking-reasons-alert"
            type={isExpired ? "warning" : "danger"}
            className="blocking-reasons-section"
          >
            <h3 className="blocking-reasons-title">
              {isExpired
                ? t("merge.errors.expired")
                : t("merge.ineligible-title")}
            </h3>
            {!isExpired && effectiveEligibility.conflicts.length > 0 && (
              <>
                <p className="blocking-reasons-description">
                  {t("merge.ineligible-description")}
                </p>
                <ul className="blocking-reasons-list">
                  {effectiveEligibility.conflicts.map((conflict) => (
                    <li key={conflict.reason}>
                      {getConflictMessage(conflict)}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Alert>
        )}

        {isSwapped && effectiveEligibility.isEligible && !isExpired && (
          <Alert
            htmlId="swap-logout-warning-alert"
            type="warning"
            className="swap-logout-warning"
          >
            {t("merge-summary.swap-logout-warning")}
          </Alert>
        )}

        <div className="consent-section">
          <CheckBoxList
            htmlId="merge-consent-checkbox"
            name="MergeConsent"
            values={consentChecked ? ["consent"] : []}
            onChange={(cxEvent) =>
              setConsentChecked(!!cxEvent.target.value.length)
            }
            options={[
              {
                value: "consent",
                label: t("merge-summary.consent-label"),
                disabled: isConsentDisabled
              }
            ]}
          />
        </div>

        <div className="actions-section">
          <SubmitButton
            htmlId="merge-submit-button"
            buttonStyle="primary"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="submit-button"
          >
            {isSubmitting
              ? t("merge-summary.submitting")
              : t("merge-summary.submit")}
          </SubmitButton>
          <Button
            htmlId="merge-cancel-button"
            buttonStyle="link"
            onClick={onCancel}
            className="cancel-button"
          >
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const conflictShape = PropTypes.shape({
  reason: PropTypes.string.isRequired,
  details: PropTypes.object
});

MergeSummaryPage.propTypes = {
  combinedAccount: accountShape.isRequired,
  conflicts: PropTypes.arrayOf(conflictShape),
  expiresAt: PropTypes.string,
  isEligible: PropTypes.bool,
  isLoadingSwap: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isSwapped: PropTypes.bool,
  onCancel: PropTypes.func,
  onResetSwap: PropTypes.func,
  onSubmit: PropTypes.func,
  onSwap: PropTypes.func,
  primaryAccount: accountShape.isRequired,
  secondAccount: accountShape.isRequired,
  swappedEligibility: PropTypes.shape({
    isEligible: PropTypes.bool,
    conflicts: PropTypes.arrayOf(conflictShape)
  })
};

export default MergeSummaryPage;
